<template>
  <button
    class="base-button"
    :class="[variant, size, { 'is-loading': loading, 'full-width': fullWidth }]"
    :disabled="disabled || loading"
    :type="type"
  >
    <span v-if="loading" class="spinner"></span>
    <i v-else-if="icon" :class="icon" class="base-button-icon"></i>
    <span v-if="$slots.default" class="btn-text">
      <slot />
    </span>
  </button>
</template>

<script setup>
/* eslint-disable no-undef */
defineProps({
  variant: {
    type: String,
    default: "primary",
    validator: (v) =>
      [
        "primary",
        "secondary",
        "danger",
        "warning",
        "ghost",
        "accent",
        "light",
        "white",
        "outline",
        "outline-danger",
      ].includes(v),
  },
  size: {
    type: String,
    default: "md",
    validator: (v) => ["sm", "md", "lg"].includes(v),
  },
  icon: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: "button",
  },
});
</script>

<style scoped>
.base-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border: none;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-base);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.base-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.base-button.full-width {
  width: 100%;
}

/* Sizes */
.base-button.sm {
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-xs);
}

.base-button.md {
  padding: var(--space-3) var(--space-5);
  font-size: var(--font-sm);
}

.base-button.lg {
  padding: var(--space-4) var(--space-6);
  font-size: var(--font-base);
}

/* Variants */
.base-button.primary {
  background: var(--gradient-primary);
  color: white;
  box-shadow: 0 2px 10px rgba(99, 102, 241, 0.3);
}

.base-button.primary:hover:not(:disabled) {
  background: var(--gradient-primary-hover);
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
  transform: translateY(-2px);
}

.base-button.secondary {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
}

.base-button.secondary:hover:not(:disabled) {
  background: var(--bg-hover);
  border-color: var(--border-accent);
}

.base-button.danger {
  background: var(--gradient-danger);
  color: white;
  box-shadow: 0 2px 10px rgba(239, 68, 68, 0.3);
}

.base-button.danger:hover:not(:disabled) {
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
  transform: translateY(-2px);
}

.base-button.warning {
  background: var(--gradient-warning);
  color: white;
  box-shadow: 0 2px 10px rgba(245, 158, 11, 0.3);
}

.base-button.warning:hover:not(:disabled) {
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.4);
  transform: translateY(-2px);
}

.base-button.ghost {
  background: transparent;
  color: var(--text-muted);
  border: 1px solid var(--border-secondary);
}

.base-button.ghost:hover:not(:disabled) {
  background: var(--bg-hover);
  color: var(--text-primary);
  border-color: var(--border-accent);
}

.base-button.accent {
  background: var(--gradient-accent);
  color: var(--text-inverse);
  box-shadow: 0 2px 10px rgba(6, 182, 212, 0.3);
}

.base-button.accent:hover:not(:disabled) {
  box-shadow: 0 4px 15px rgba(6, 182, 212, 0.4);
  transform: translateY(-2px);
}

.base-button.light {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
}

.base-button.light:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
}

.base-button.white {
  background: white;
  color: #667eea;
  font-weight: 600;
}

.base-button.white:hover:not(:disabled) {
  background: #f8f9ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
}

.base-button.outline {
  background: transparent;
  color: var(--accent-primary);
  border: 1px solid var(--accent-primary);
}

.base-button.outline:hover:not(:disabled) {
  background: var(--accent-primary);
  color: white;
}

.base-button.outline-danger {
  background: transparent;
  color: var(--accent-danger);
  border: 1px solid var(--accent-danger);
}

.base-button.outline-danger:hover:not(:disabled) {
  background: var(--accent-danger);
  color: white;
}

/* Icon */
/* Not ".btn-icon": that is the global square row-action button in theme.css, and the
   name clash boxed the icon (padding/aspect-ratio). */
.base-button-icon {
  font-size: 1em;
}

/* Spinner */
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.is-loading .btn-text {
  opacity: 0.7;
}
</style>
